import { useState, useCallback, useRef, useEffect } from 'react';
import { dataExchangeApi, downloadBlob } from '../services/api/endpoints/dataExchange';
import type {
  SupportedFormat,
  FormatDetectionResult,
  PreviewResult,
  ImportConfig,
  ImportFromUrlRequest,
  ImportTaskStatus,
  ValidationResult,
  ExportEntry,
  ExportConfig,
  ExportFiscalYearParams,
  OperationStatus
} from '../types/data-exchange';
import toast from 'react-hot-toast';

// ====== POLLING CONFIG ======
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 600_000; // 10 min max

// ====== TYPES HOOK ======

interface UseDataExchangeReturn {
  // État
  loading: boolean;
  error: string | null;

  // Formats
  importFormats: SupportedFormat[];
  exportFormats: SupportedFormat[];
  loadImportFormats: () => Promise<void>;
  loadExportFormats: () => Promise<void>;

  // Détection & prévisualisation
  detectFormat: (file: File) => Promise<FormatDetectionResult | null>;
  previewFile: (file: File, format?: string, maxLines?: number) => Promise<PreviewResult | null>;

  // Import
  importFromUrl: (request: ImportFromUrlRequest) => Promise<string | null>;
  importUpload: (fiscalYearId: string, file: File, config: ImportConfig) => Promise<boolean>;
  
  // Validation
  validateFile: (file: File, config: ImportConfig) => Promise<ValidationResult | null>;

  // Tâches d'import
  activeTask: ImportTaskStatus | null;
  taskHistory: ImportTaskStatus[];
  pollTaskStatus: (taskId: string) => void;
  stopPolling: () => void;
  cancelTask: (taskId: string) => Promise<boolean>;
  loadTaskHistory: () => Promise<void>;

  // Export
  exportFiscalYear: (fiscalYearId: string, params: ExportFiscalYearParams) => Promise<boolean>;
  exportFec: (fiscalYearId: string, siren: string, closingDate: string) => Promise<boolean>;
  exportEntries: (entries: ExportEntry[], config: ExportConfig) => Promise<boolean>;

  // Templates
  downloadTemplate: (format: string) => Promise<boolean>;
}

/**
 * Hook principal pour l'échange de données comptables (import/export).
 * Gère le cycle complet : détection → prévisualisation → import/export → suivi.
 */
export function useDataExchange(): UseDataExchangeReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importFormats, setImportFormats] = useState<SupportedFormat[]>([]);
  const [exportFormats, setExportFormats] = useState<SupportedFormat[]>([]);
  const [activeTask, setActiveTask] = useState<ImportTaskStatus | null>(null);
  const [taskHistory, setTaskHistory] = useState<ImportTaskStatus[]>([]);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartRef = useRef<number>(0);

  // Nettoyage à la destruction
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // ====== FORMATS ======

  const loadImportFormats = useCallback(async () => {
    try {
      const response = await dataExchangeApi.getImportFormats();
      if (response.success && response.data) {
        const data = (response.data as any).formats || response.data;
        setImportFormats(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading import formats:', err);
    }
  }, []);

  const loadExportFormats = useCallback(async () => {
    try {
      const response = await dataExchangeApi.getExportFormats();
      if (response.success && response.data) {
        const data = (response.data as any).formats || response.data;
        setExportFormats(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading export formats:', err);
    }
  }, []);

  // ====== DÉTECTION DE FORMAT ======

  const detectFormat = useCallback(async (file: File): Promise<FormatDetectionResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.detectFormat(file);
      if (result.success && result.data) {
        return result.data;
      }
      setError(result.error || 'Échec de la détection du format');
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== PRÉVISUALISATION ======

  const previewFile = useCallback(async (file: File, format?: string, maxLines?: number): Promise<PreviewResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.preview(file, format, maxLines);
      if (result.success && result.data) {
        return result.data;
      }
      setError(result.error || 'Échec de la prévisualisation');
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== IMPORT DEPUIS URL (ASYNCHRONE) ======

  const importFromUrl = useCallback(async (request: ImportFromUrlRequest): Promise<string | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await dataExchangeApi.importFromUrl(request);
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        const taskId = data.taskId;
        toast.success('Import démarré. Suivi en cours...');
        // Commencer le polling automatiquement
        pollTaskStatus(taskId);
        return taskId;
      }
      const errMsg = response.error || 'Échec du démarrage de l\'import';
      setError(errMsg);
      toast.error(errMsg);
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== IMPORT UPLOAD (SYNCHRONE) ======

  const importUpload = useCallback(async (fiscalYearId: string, file: File, config: ImportConfig): Promise<boolean> => {
    console.log('📡 useDataExchange.importUpload entrée', { fiscalYearId, fileName: file?.name, config });
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Appel dataExchangeApi.importUpload...');
      const result = await dataExchangeApi.importUpload(fiscalYearId, file, config);
      console.log('📡 Résultat importUpload:', result);
      if (result.success && result.data) {
        const data = result.data;
        // Vérifier aussi data.success (le backend peut retourner HTTP 201 avec success: false)
        if (data.success === false) {
          const errMsg = data.message || 'Import échoué';
          const details = data.errors?.map((e: any) => e.message || e).join('; ') || '';
          setError(details || errMsg);
          toast.error(errMsg);
          if (data.warnings?.length) {
            console.warn('⚠️ Import warnings:', data.warnings.slice(0, 5));
          }
          return false;
        }
        const count = data.importedCount || 0;
        toast.success(`Import terminé : ${count} lignes importées`);
        if (data.errorCount > 0) {
          toast.error(`${data.errorCount} erreur(s) lors de l'import`);
        }
        return true;
      }
      const errMsg = result.error || 'Échec de l\'import';
      setError(errMsg);
      toast.error(errMsg);
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== VALIDATION ======

  const validateFile = useCallback(async (file: File, config: ImportConfig): Promise<ValidationResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.validate(file, config);
      if (result.success && result.data) {
        return result.data;
      }
      setError(result.error || 'Échec de la validation');
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== POLLING DE TÂCHE ======

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollTaskStatus = useCallback((taskId: string) => {
    // Arrêter un polling précédent
    stopPolling();
    pollingStartRef.current = Date.now();

    const poll = async () => {
      try {
        const response = await dataExchangeApi.getTaskStatus(taskId);
        if (response.success && response.data) {
          const status = (response.data as any).data || response.data;
          setActiveTask(status);

          const terminalStatuses: OperationStatus[] = ['completed', 'failed', 'partial', 'cancelled'];
          if (terminalStatuses.includes(status.status)) {
            stopPolling();

            if (status.status === 'completed') {
              toast.success(`Import terminé : ${status.importedLines} lignes importées`);
            } else if (status.status === 'failed') {
              toast.error('L\'import a échoué');
            } else if (status.status === 'partial') {
              toast.success(`Import partiel : ${status.importedLines}/${status.totalLines} lignes`);
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }

      // Timeout global
      if (Date.now() - pollingStartRef.current > MAX_POLL_DURATION_MS) {
        stopPolling();
        toast.error('Timeout : le suivi de l\'import a été interrompu');
      }
    };

    // Premier appel immédiat
    poll();
    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // ====== ANNULATION ======

  const cancelTask = useCallback(async (taskId: string): Promise<boolean> => {
    try {
      const response = await dataExchangeApi.cancelTask(taskId);
      if (response.success) {
        stopPolling();
        setActiveTask(prev => prev ? { ...prev, status: 'cancelled' as OperationStatus } : null);
        toast.success('Tâche annulée');
        return true;
      }
      toast.error('Impossible d\'annuler la tâche');
      return false;
    } catch (err) {
      toast.error('Erreur lors de l\'annulation');
      return false;
    }
  }, [stopPolling]);

  // ====== HISTORIQUE ======

  const loadTaskHistory = useCallback(async () => {
    try {
      const response = await dataExchangeApi.listTasks();
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setTaskHistory(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading task history:', err);
    }
  }, []);

  // ====== EXPORT ======

  const exportFiscalYear = useCallback(async (fiscalYearId: string, params: ExportFiscalYearParams): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.exportFiscalYear(fiscalYearId, params);
      if (result.success && result.data) {
        downloadBlob(result.data.blob, result.data.filename);
        toast.success('Export téléchargé');
        return true;
      }
      setError(result.error || 'Échec de l\'export');
      toast.error('Échec de l\'export');
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportFec = useCallback(async (fiscalYearId: string, siren: string, closingDate: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.exportFec(fiscalYearId, { siren, closingDate });
      if (result.success && result.data) {
        downloadBlob(result.data.blob, result.data.filename);
        toast.success('FEC exporté');
        return true;
      }
      setError(result.error || 'Échec de l\'export FEC');
      toast.error('Échec de l\'export FEC');
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportEntries = useCallback(async (entries: ExportEntry[], config: ExportConfig): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const result = await dataExchangeApi.exportEntries(entries, config);
      if (result.success && result.data) {
        downloadBlob(result.data.blob, result.data.filename);
        toast.success('Export téléchargé');
        return true;
      }
      setError(result.error || 'Échec de l\'export');
      toast.error('Échec de l\'export');
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur réseau';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====== TEMPLATES ======

  const downloadTemplate = useCallback(async (format: string): Promise<boolean> => {
    try {
      const result = await dataExchangeApi.downloadSampleTemplate(format);
      if (result.success && result.data) {
        downloadBlob(result.data.blob, result.data.filename);
        toast.success('Template téléchargé');
        return true;
      }
      toast.error('Impossible de télécharger le template');
      return false;
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
      return false;
    }
  }, []);

  return {
    loading,
    error,
    importFormats,
    exportFormats,
    loadImportFormats,
    loadExportFormats,
    detectFormat,
    previewFile,
    importFromUrl,
    importUpload,
    validateFile,
    activeTask,
    taskHistory,
    pollTaskStatus,
    stopPolling,
    cancelTask,
    loadTaskHistory,
    exportFiscalYear,
    exportFec,
    exportEntries,
    downloadTemplate
  };
}
